import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import newsBackground from '../../assets/images/news_background.png';
import { dashboardService } from '../../services/api';
import { useTranslation } from '../../contexts/TranslationContext';
import NewsImageWithFallback from '../news/NewsImageWithFallback';

const LANGUAGE_LABELS = {
  en: "English", de: "Deutsch", fr: "Français", es: "Español", it: "Italiano",
  pt: "Português", nl: "Nederlands", pl: "Polski", hr: "Hrvatski",
};

// Some providers stuff a (often malformed) URL into the author field, e.g.
// "https://www. facebook. com/DailyMail". Hide those rather than render
// "By https://www. facebook. com/...". Mirrors the looksLikeUrl guard used
// on the article detail page.
const looksLikeUrl = (s) => {
  if (!s) return false;
  const t = String(s).trim();
  return /^(https?:\/\/|www\.)/i.test(t) || /\.(com|net|org|io|co|news)\b/i.test(t.replace(/\s+/g, ""));
};

const NewsCarousel = () => {
  const navigate = useNavigate();
  const { currentLanguage, changeLanguage } = useTranslation();
  const apiLanguage = (currentLanguage || "en").toLowerCase().split("-")[0];
  const localeName = LANGUAGE_LABELS[apiLanguage] || apiLanguage.toUpperCase();
  const [news, setNews] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    const fetchNews = async () => {
      setLoading(true);
      try {
        const language = currentLanguage && currentLanguage.toLowerCase() !== 'en' ? currentLanguage.toLowerCase() : null;
        const response = await dashboardService.getLatestNews(10, 0, language);
        if (response.success && response.data?.results) {
          setNews(response.data.results);
          // Reset to first slide when news changes
          setCurrentSlide(0);
        }
      } catch (error) {
        console.error('Failed to fetch news:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [currentLanguage]);

  // Auto-rotation effect
  useEffect(() => {
    if (news.length <= 1 || isPaused) return;

    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev === news.length - 1 ? 0 : prev + 1));
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [news.length, isPaused]);

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? news.length - 1 : prev - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => (prev === news.length - 1 ? 0 : prev + 1));
  };

  if (loading) {
    return <div className="bg-[#1A1F36] text-white rounded-lg h-[500px] animate-pulse" />;
  }

  if (news.length === 0) {
    return (
      <div className="bg-[#1A1F36] text-white rounded-lg h-[500px] relative overflow-hidden">
        {/* Background Football Image */}
        <div className="absolute top-0 right-[-5%] h-full w-[55%] opacity-30">
          <img 
            src={newsBackground} 
            alt="" 
            className="h-full w-full object-contain"
          />
          <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#1A1F36]/20 to-[#1A1F36]" />
        </div>

        {/* Empty State Message */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <svg 
              className="w-16 h-16 mx-auto mb-4 text-secondary" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2 2 0 00-2-2h-2" 
              />
            </svg>
            <h3 className="text-xl font-medium mb-2">No News Available</h3>
            {apiLanguage !== "en" ? (
              <>
                <p className="text-gray-400">
                  Currently no football news in {localeName}.
                </p>
                <button
                  type="button"
                  onClick={() => changeLanguage("en")}
                  className="mt-4 inline-flex items-center px-4 py-2 rounded-lg bg-[#4A90D9] hover:bg-[#2F78C4] text-white text-sm font-semibold transition-colors"
                >
                  Switch to English
                </button>
              </>
            ) : (
              <p className="text-gray-400">Check back later for the latest updates</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const currentNews = news[currentSlide];

  // Safety check for current news
  if (!currentNews) {
    return (
      <div className="bg-[#1A1F36] text-white rounded-lg h-[500px] relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <h3 className="text-xl font-medium mb-2">Loading News...</h3>
            <p className="text-gray-400">Please wait while we fetch the latest updates</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="bg-[#1A1F36] text-white rounded-lg h-[500px] relative overflow-hidden"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      {/* Background Football Image - Adjusted positioning */}
      <div className="absolute top-0 right-[-5%] h-full w-[55%]">
        <img 
          src={newsBackground} 
          alt="" 
          className="h-full w-full object-contain"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#1A1F36]/20 to-[#1A1F36]" />
      </div>

      {/* Navigation Arrows - Only show if there are multiple news items */}
      {news.length > 1 && (
        <div className="absolute top-10 md:top-8 right-8 flex space-x-2 z-10">
          <button 
            onClick={handlePrevSlide}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Previous news"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button 
            onClick={handleNextSlide}
            className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
            aria-label="Next news"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}



      {/* Content Section - back to the ORIGINAL stacked layout: meta row, then
          the image (top, left), then title + description + author, with the
          "View more" button pinned to the bottom via mt-auto. No justify-center
          and no extra top padding, so the image sits exactly where it did in
          the original (top of the card), not vertically re-centered.

          The ONLY change vs the original is the text truncation: title and
          description are both line-clamp-2 (+ overflow-hidden + text-ellipsis)
          so long copy ends cleanly with "..." instead of a hard mid-line cut.
          No fade overlay, no side-by-side row. */}
      <div className="relative h-full p-4 pb-6 md:p-8 md:pb-8 flex flex-col">
        <div className="flex flex-col min-h-0 overflow-hidden mb-4 md:mb-6">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xs md:text-sm font-medium uppercase tracking-wide">
              {currentNews.category?.toUpperCase() || "SPORTS NEWS"}
            </span>
            {currentNews.source && (
              <>
                <span className="text-gray-400">•</span>
                <span className="text-sm text-gray-300">{currentNews.source}</span>
              </>
            )}
          </div>

          {/* Image: whole image, never cropped (contain) on a neutral ground.
              Same position (top, left) and size as the original. */}
          <button
            type="button"
            onClick={() => currentNews.id && navigate(`/news/${encodeURIComponent(currentNews.id)}`)}
            disabled={!currentNews.id}
            className="block w-fit mb-6 group"
            aria-label={currentNews.title ? `Open article: ${currentNews.title}` : "Open article"}
          >
            <div className="rounded-lg overflow-hidden bg-[#0F1430] transition-transform group-hover:scale-[1.02] w-[200px] h-[150px] md:w-[240px] md:h-[180px]">
              <NewsImageWithFallback
                imageUrl={currentNews.image}
                alt={currentNews.title || 'News image'}
                sourceName={currentNews.source}
                className="w-full h-full"
                rounded=""
              />
            </div>
          </button>

          <h3
            onClick={() => currentNews.id && navigate(`/news/${encodeURIComponent(currentNews.id)}`)}
            className={`text-xl leading-tight md:leading-[1.3] font-bold w-full md:w-[60%] mb-3 flex-shrink-0 overflow-hidden text-ellipsis line-clamp-2 ${currentNews.id ? "cursor-pointer hover:text-[#4A90D9] transition-colors" : ""}`}
          >
            {currentNews.title || "Latest Sports News Update"}
          </h3>

          {currentNews.description && (
            <p className="text-gray-300 text-sm w-full md:w-[65%] mb-4 flex-shrink-0 overflow-hidden text-ellipsis line-clamp-2">
              {currentNews.description}
            </p>
          )}

          {(() => {
            const showAuthor = currentNews.author && !looksLikeUrl(currentNews.author);
            if (!showAuthor && !currentNews.published_at) return null;
            return (
              <div className="flex items-center gap-2 text-sm text-gray-400 mb-2 min-w-0">
                {showAuthor && <span className="truncate">Written by {currentNews.author}</span>}
                {showAuthor && currentNews.published_at && <span className="flex-shrink-0">•</span>}
                {currentNews.published_at && (
                  <span className="flex-shrink-0">{new Date(currentNews.published_at).toLocaleDateString()}</span>
                )}
              </div>
            );
          })()}
        </div>

        {/* "View more" button: pinned to the bottom (mt-auto), kept off the
            card edge by the container's bottom padding. */}
        <button
          onClick={() => navigate("/news")}
          className="mt-auto flex-shrink-0 inline-flex items-center px-4 py-2 md:px-6 md:py-3 rounded-lg transition-colors text-sm md:text-base font-medium w-full md:w-fit justify-center bg-[#2283FF] text-white hover:bg-blue-600 cursor-pointer"
        >
          View more news
          <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default NewsCarousel; 